'use client'

import * as React from 'react'
import { BarChart3 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { trackEvent } from '@/lib/analytics'

interface ToolDataChartProps {
  /** Section title shown above the chart. */
  title: string
  /** Optional single-line subtitle under the title. */
  subtitle?: string
  /** The chart itself, typically a Recharts `<ResponsiveContainer>` block. */
  children: React.ReactNode
  /**
   * 1–2 sentence interpretation shown below the chart. Conversational tone, no em-dashes.
   * If omitted, the footer is skipped.
   */
  footer?: React.ReactNode
  /**
   * Stable id used to namespace analytics events. The chart fires
   * `tool_chart_viewed` once per session when it scrolls into view.
   */
  trackingId: string
  /** Chart canvas height, in px. Defaults to 320. */
  height?: number
  /** Extra classes for the outer wrapper card. */
  className?: string
}

export function ToolDataChart({
  title,
  subtitle,
  children,
  footer,
  trackingId,
  height = 320,
  className,
}: ToolDataChartProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const hasFiredRef = React.useRef(false)

  React.useEffect(() => {
    const node = containerRef.current
    if (!node || hasFiredRef.current) return
    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !hasFiredRef.current) {
            hasFiredRef.current = true
            trackEvent('tool_chart_viewed', { tool_chart: trackingId })
            observer.disconnect()
            break
          }
        }
      },
      { threshold: 0.4 },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [trackingId])

  return (
    <Card
      ref={containerRef}
      className={cn(
        'p-5 md:p-6 bg-white border-gray-200',
        className,
      )}
    >
      <header className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-green-50 rounded-lg shrink-0">
          <BarChart3 className="w-5 h-5 text-green-700" aria-hidden="true" />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 leading-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
      </header>

      <div
        className="w-full"
        style={{ height, minWidth: 0 }}
        role="img"
        aria-label={subtitle ? `${title}. ${subtitle}` : title}
      >
        {children}
      </div>

      {footer && (
        <p className="text-sm text-gray-700 mt-4 leading-relaxed">{footer}</p>
      )}
    </Card>
  )
}

/**
 * Pulsing skeleton used as the loading state when a chart body is lazy-loaded
 * via next/dynamic. Mirrors the soil-temp chart skeleton aesthetic but works
 * for any height.
 */
export function ToolDataChartSkeleton({
  height = 320,
}: {
  height?: number
}) {
  return (
    <Card className="p-5 md:p-6 bg-white border-gray-200">
      <div className="flex items-start gap-3 mb-4">
        <Skeleton className="w-9 h-9 rounded-lg" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
      <div
        className="bg-gray-50 rounded-lg relative overflow-hidden animate-pulse"
        style={{ height }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-10 border-r border-gray-200" />
        <div className="absolute left-10 right-0 bottom-0 h-7 border-t border-gray-200" />
        <div className="absolute left-10 right-0 top-0 bottom-7 flex flex-col justify-between p-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-px bg-gray-200" />
          ))}
        </div>
      </div>
      <Skeleton className="h-4 w-3/4 mt-4" />
    </Card>
  )
}
